import { Component, EventEmitter, Input, OnInit, Output, Self } from '@angular/core';
import { defer, noop, Observable, of } from 'rxjs';
import { UntypedFormControl, NgControl } from '@angular/forms';
import { ValidatorCheck } from '@shared/form-input/utils/validator.check';
import { debounceTime, delay, distinctUntilChanged, finalize, startWith, switchMap } from 'rxjs/operators';

export interface AutocompleteRemoteConfig {
	dataHandler: (searchQuery: string) => Observable<string[]>;
	debounceTimeInMs?: number;
	delayInMs?: number;
}

@Component({
    selector: 'pim-input-autocomplete',
    templateUrl: './input-autocomplete.component.html',
    styleUrls: ['./input-autocomplete.component.sass'],
    standalone: false
})
export class InputAutocompleteComponent implements OnInit {
	@Input()
	id!: string;
	@Input()
	tabindex!: number;
	@Input()
	label: string = '';

	@Input()
	autocompleteConfig!: AutocompleteRemoteConfig;

	@Input()
	readonly: boolean = false;

	@Output()
	valueSelected: EventEmitter<string> = new EventEmitter<string>();

	required: boolean = false;

	dataObs$!: Observable<string[]> | undefined;
	dataObsExecuting: boolean = false;

	private internalValue: any = '';
	private onChangeCallback: (_: any) => void = noop;
	control!: UntypedFormControl;

	// form-change triggers also on mat-option select, save it to not make multiple requests
	private lastSelected: string = '';

	constructor(private validatorCheck: ValidatorCheck, @Self() public ngControl: NgControl) {
		this.ngControl.valueAccessor = this;
	}

	ngOnInit(): void {
		this.control = this.ngControl.control as UntypedFormControl;
		this.required = this.validatorCheck.isRequired(this.control);
		this.createFormControlChangeListener();
	}

	private createFormControlChangeListener(): void {
		this.dataObs$ = this.control.valueChanges.pipe(
			// User-Input handling pipes
			startWith(''),
			delay(this.autocompleteConfig.delayInMs || 300),
			debounceTime(this.autocompleteConfig.debounceTimeInMs || 300),
			distinctUntilChanged(),
			// Now return the actual data-call
			switchMap((value: string) => {
				// Return empty-list if the form is empty or if the change is the selected (click on option)
				if (!value || value === this.lastSelected) {
					return of([]);
				}
				// return deferred obs to handle the loader
				return defer(() => {
					this.dataObsExecuting = true;
					return this.autocompleteConfig.dataHandler(value).pipe(
						finalize(() => {
							this.dataObsExecuting = false;
						})
					);
				});
			})
		);
	}

	get fullLabel(): string {
		const requiredAstersik = this.required ? ' *' : '';
		return `${this.label}${requiredAstersik}`;
	}

	get value(): any {
		return this.internalValue;
	}

	set value(v: any) {
		if (v !== this.internalValue) {
			this.internalValue = v?.toString() || v?.trim();
			this.onChangeCallback(this.internalValue);
		}
	}

	writeValue(obj: any): void {
		if (this.value !== obj) {
			this.value = obj;
		}
	}

	registerOnChange(fn: any): void {
		this.onChangeCallback = fn;
	}

	registerOnTouched(): void {
	}

	handleOptionSelect(selectedOption: string): void {
		this.lastSelected = selectedOption;
		this.valueSelected.emit(selectedOption);
	}
}
